import { ReactElement, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'antd';
import {
  Crud,
  CrudFormComponent,
  CrudMode,
  CrudProps,
  notification,
  rejectionError,
  transformCrudFormErrors,
  ValidationError,
} from '@ohpen/enso-ui';

import { RegionResponse, regionCreate, regionUpdate } from '@pmp/api';
import { CrudFormProps } from '@pmp/types';

import { getApiErrorMessage } from '@pmp/utils';
import { TagListPanel } from '@pmp/components';

import { getRegionSchema } from '../regionsBdmsFormConfig';
import { toCrudFieldErrors } from '../utils';
import styles from '../styles.module.css';

type RegionFormValues = { name: string };

type Props = CrudFormProps<RegionResponse> & { bdmNameById: Map<string, string> };

export const RegionCrudForm = ({ mode, record, bdmNameById, onClose, refreshTable }: Props): ReactElement => {
  const ref = useRef<CrudFormComponent>(null);
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  const renderWarning = useCallback(
    (): ReactNode => (
      <Alert
        type="warning"
        showIcon
        title={t('lender.regionsBdms.labels.noChangeRequestSaveWarning')}
        className={styles.noChangeRequestAlert}
      />
    ),
    [t],
  );

  const linkedBdms = useMemo(
    () =>
      (record?.bdmIds ?? [])
        .map(id => ({ key: id, label: bdmNameById.get(id) ?? id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [record, bdmNameById],
  );

  const renderLinkedBdms = useCallback(
    (): ReactNode => (
      <TagListPanel
        items={linkedBdms}
        header={t('lender.regionsBdms.labels.linkedBdmsCount', { count: linkedBdms.length })}
        emptyText={t('lender.regionsBdms.labels.noLinkedBdms')}
      />
    ),
    [linkedBdms, t],
  );

  const onConfirm = useCallback(
    async (values: RegionFormValues): Promise<ValidationError[] | void> => {
      try {
        if (mode === CrudMode.EDIT && record) {
          await regionUpdate({ path: { id: record.id }, body: { name: values.name } });
          notification().success({ title: t('lender.regionsBdms.labels.regionUpdated', { name: values.name }) });
        } else {
          await regionCreate({ body: { name: values.name } });
          notification().success({ title: t('lender.regionsBdms.labels.regionCreated', { name: values.name }) });
        }
        refreshTable();
      } catch (error) {
        const fieldErrors = toCrudFieldErrors(error);
        if (fieldErrors) return transformCrudFormErrors(fieldErrors);
        return Promise.reject(rejectionError(getApiErrorMessage(error)));
      }
    },
    [mode, record, refreshTable, t],
  );

  const crudProps: CrudProps<RegionFormValues> = useMemo(
    () => ({
      ref,
      open,
      schema: getRegionSchema(t, {
        warning: renderWarning,
        linkedBdms: mode === CrudMode.CREATE ? undefined : renderLinkedBdms,
      }),
      title: mode === CrudMode.EDIT ? t('lender.regionsBdms.labels.editRegion') : t('lender.regionsBdms.labels.createRegion'),
      labels: { confirm: t('enso.buttons.save') },
      initialValues: record ? { name: record.name } : null,
      mode,
      onClose: handleOnClose,
      onConfirm,
    }),
    [open, mode, record, t, renderWarning, renderLinkedBdms, handleOnClose, onConfirm],
  );

  return <Crud {...crudProps} />;
};
